import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Chip,
  Stack,
  Link,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DataTable from "../../pages/Requirement/components/common/ReusableTable";
import { fetchData } from "../../services/BackendServices";

const themeColor = "#de6b14";

export default function BankInsight() {
  const [activeTab, setActiveTab] = useState("bank-route");
  const [isLoading, setIsLoading] = useState(true);
  const [bankItems, setBankItems] = useState([]);

  // Selections
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductCountry, setSelectedProductCountry] = useState(null);

  // Table
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Unique countries & products
  const uniqueCountries = useMemo(() => {
    return [...new Set(bankItems.map(i => i.country).filter(Boolean))].sort();
  }, [bankItems]);

  const uniqueProducts = useMemo(() => {
    const set = new Set();
    bankItems.forEach(i => {
      const p = i.product;
      if (Array.isArray(p)) p.forEach(x => set.add(x.trim()));
      else if (typeof p === "string")
        p.split(/[,;\n]+/).map(x => x.trim()).filter(Boolean).forEach(x => set.add(x));
    });
    return Array.from(set).sort();
  }, [bankItems]);

  // Countries that offer the selected product
  const countriesOfferingProduct = useMemo(() => {
    if (!selectedProduct) return [];
    const target = selectedProduct.toLowerCase().trim();
    const filtered = bankItems.filter(i => {
      const p = i.product;
      if (Array.isArray(p)) return p.some(x => x.toLowerCase().trim() === target);
      if (typeof p === "string")
        return p.toLowerCase().split(/[,;\n]+/).some(x => x.trim() === target);
      return false;
    });
    return [...new Set(filtered.map(i => i.country))].sort();
  }, [selectedProduct, bankItems]);

  const fetchBankData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchData({
        target: "listBankTrades",
        userOrder: "Id",
        rowStart: 0,
        pageSize: 10000,
        filterTerms: {},
      });
      setBankItems(response?.items || []);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBankData();
  }, [fetchBankData]);

  // Group banks by country + institution_name
  const groupAndSortBanks = (items) => {
    const map = new Map();

    items.forEach(item => {
      const key = `${item.country}|${item.institution_name}`;
      if (!map.has(key)) {
        map.set(key, {
          country: item.country,
          institutionName: item.institution_name,
          licenseType: item.license_type || "N/A",
          identificationNo: item.identification_no || "N/A",
          headOffice: item.head_office || "N/A",
          allProducts: new Set(),
        });
      }

      const products = item.product;
      if (Array.isArray(products)) {
        products.forEach(p => map.get(key).allProducts.add(p.trim()));
      } else if (typeof products === "string") {
        products.split(/[,;\n]+/).map(p => p.trim()).filter(Boolean).forEach(p => map.get(key).allProducts.add(p));
      }
    });

    return Array.from(map.values())
      .map(bank => ({
        ...bank,
        productsList: Array.from(bank.allProducts).sort(),
      }))
      .sort((a, b) => a.institutionName.localeCompare(b.institutionName));
  };

  // Bank Tab: Explore by Country
  const handleExploreByCountry = () => {
    if (!selectedCountry) return;
    const filtered = bankItems.filter(i => i.country === selectedCountry);
    setTableData(groupAndSortBanks(filtered));
    setShowTable(true);
    setExpandedRows(new Set());
  };

  // Product Tab: Explore by Product + Country (both required)
  const handleExploreByProduct = () => {
    if (!selectedProduct || !selectedProductCountry) return;

    const target = selectedProduct.toLowerCase().trim();

    const filtered = bankItems.filter(item => {
      const p = item.product;
      const hasProduct = Array.isArray(p)
        ? p.some(x => x.toLowerCase().trim() === target)
        : typeof p === "string"
        ? p.toLowerCase().split(/[,;\n]+/).some(x => x.trim() === target)
        : false;

      return hasProduct && item.country === selectedProductCountry;
    });

    setTableData(groupAndSortBanks(filtered));
    setShowTable(true);
    setExpandedRows(new Set());
  };

  const toggleExpand = (name) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const columns = [
    { id: "country", label: "Country", minWidth: 100 },
    { id: "institutionName", label: "Institution Name", minWidth: 280 },
    { id: "licenseType", label: "License Type", minWidth: 160 },
    { id: "identificationNo", label: "Identification Number", minWidth: 150 },
    { id: "headOffice", label: "Head Office", minWidth: 200 },
    {
      id: "productsList",
      label: "Trade Finance Product",
      minWidth: 380,
      renderCell: (row) => {
        const products = row.productsList;
        const isExpanded = expandedRows.has(row.institutionName);
        const visible = isExpanded ? products : products.slice(0, 3);
        const hiddenCount = products.length - 3;

        return (
          <Stack spacing={1}>
            <Stack direction="row" flexWrap="wrap" gap={0.8}>
              {visible.map((prod, i) => (
                <Chip
                  key={i}
                  label={prod}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: "0.8rem", height: 28 }}
                />
              ))}
            </Stack>
            {hiddenCount > 0 && (
              <Link
                component="button"
                variant="body2"
                color="primary"
                underline="none"
                onClick={() => toggleExpand(row.institutionName)}
                sx={{ fontWeight: 600 }}
              >
                {isExpanded ? "View less" : `View more (+${hiddenCount})`}
              </Link>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <div className="py-4 card mt-2">
      {/* Header */}
      <Box textAlign="center" mb={5}>
        <Typography
                  variant="h3"
                  fontWeight="bold"
                  color="#525252"
                  mt={2}
                  sx={{ fontSize: "30px !important" }}
                >
          Bank Explorer:
        </Typography>
        <Typography
                  variant="h4"
                  fontWeight={500}
                  color="#525252"
                  sx={{ fontSize: "30px !important" }}
                >
          Your Gateway to Global Finance Opportunities
        </Typography>
        <Typography
                  variant="body1"
                  mt={3}
                  className="lead col-lg-8 mx-auto"
                  sx={{ fontSize: "18px !important", fontWeight: "400" }}
                >
                  Identify products that move across Africa-Middle East routes
                  <br />
                  or find top sourcing and export markets for any agricultural
                  commodity.
                </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          setShowTable(false);
          setSelectedCountry(null);
          setSelectedProduct(null);
          setSelectedProductCountry(null);
          setExpandedRows(new Set());
        }}
        centered
          sx={{
            bgcolor: "white",
            "& .MuiTabs-indicator": { display: "none" },
          }}
      >
        <Tab
          label="Bank Explorer (Country Focus)"
          value="bank-route"
          sx={{
            fontSize: "13px !important",
            textTransform: "none",
            fontWeight: 600,
            backgroundColor:
              activeTab === "bank-route" ? themeColor : "#f5f3f3",
            color: activeTab === "bank-route" ? "#fff !important" : "#555",
            "&:hover": {
              backgroundColor:
                activeTab === "bank-route" ? themeColor : "#f0f0f0",
            },
          }}
        />
        <Tab
          label="Product Market Insights (Product Focus)"
          value="product"
          sx={{
            fontSize: "13px !important",
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: activeTab === "product" ? themeColor : "#f5f3f3",
            color: activeTab === "product" ? "#fff !important" : "#555",
            "&:hover": {
              backgroundColor:
                activeTab === "product" ? themeColor : "#f0f0f0",
            },
          }}
        />
      </Tabs>

      {/* Form */}
      <Box sx={{ p: 4, bgcolor: "#f9f9f9", borderRadius: 2, mt: 3 }}>
        {/* Country Tab */}
        {activeTab === "bank-route" && (
          <div className="row g-4 align-items-end">
            <div className="col-md-5">
              <Typography className="fw-semibold mb-2">
                Country <span className="text-danger">*</span>
              </Typography>
              <Autocomplete
                options={uniqueCountries}
                value={selectedCountry}
                onChange={(_, v) => setSelectedCountry(v)}
                renderInput={(p) => <TextField {...p} placeholder="---Select Country---" />}
                disabled={isLoading}
              />
            </div>
            <div className="col-12 text-center">
              <Button
                variant="contained"
                className="submit-btn-more"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleExploreByCountry}
                disabled={!selectedCountry}
                sx={{
                    minWidth: 180,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    bgcolor: themeColor,
                    "&:hover": { bgcolor: "#c55f12" },
                    "&:disabled": { bgcolor: "#e0e0e0", color: "#aaa" },
                  }}
              >
                Explore Banks
              </Button>
            </div>
          </div>
        )}

        {/* Product Tab - Product + Country both required */}
        {activeTab === "product" && (
          <div className="row g-4 align-items-end">
            <div className="col-md-6">
              <Typography className="fw-semibold mb-2">
                Select Product <span className="text-danger">*</span>
              </Typography>
              <Autocomplete
                options={uniqueProducts}
                value={selectedProduct}
                onChange={(_, v) => {
                  setSelectedProduct(v);
                  setSelectedProductCountry(null);
                }}
                renderInput={(p) => <TextField {...p} placeholder="---Select Product---" />}
              />
            </div>
            <div className="col-md-6">
              <Typography className="fw-semibold mb-2">
                Country <span className="text-danger">*</span>
              </Typography>
              <Autocomplete
                options={countriesOfferingProduct}
                value={selectedProductCountry}
                onChange={(_, v) => setSelectedProductCountry(v)}
                disabled={!selectedProduct}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder={
                      selectedProduct
                        ? countriesOfferingProduct.length === 0
                          ? "No countries found"
                          : "---Select Country---"
                        : "---Select Product First---"
                    }
                  />
                )}
              />
            </div>
            <div className="col-12 text-center mt-4">
              <Button
                variant="contained"
                className="submit-btn-more"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleExploreByProduct}
                disabled={!selectedProduct || !selectedProductCountry}
                sx={{
                    minWidth: 180,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    bgcolor: themeColor,
                    "&:hover": { bgcolor: "#c55f12" },
                    "&:disabled": { bgcolor: "#e0e0e0", color: "#aaa" },
                  }}
              >
                Explore Banks
              </Button>
            </div>
          </div>
        )}
      </Box>

      {/* Result Table */}
      {showTable && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            {/* {activeTab === "bank-route"
              ? `Finance Opportunities in ${selectedCountry}`
              : `Finance Market Insights - "${selectedProduct}" in ${selectedProductCountry}`}
            <span style={{ color: "#666", fontWeight: "normal", marginLeft: 8 }}>
              ({tableData.length} institutions found)
            </span> */}
          </Typography>
          <DataTable
            columns={columns}
            data={tableData}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={isLoading}
          />
        </Box>
      )}
    </div>
  );
}